import React, { useState } from 'react';
import './Units.css';
import { useContract } from '../hooks/useContract';
import { PACKAGE_INFO, PACKAGE_PRICES } from '../config/contract';
import { formatNumber } from '../utils/formatters';
import Loader from './Loader';

const Units = ({ packages, userId, onSuccess, addToast }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const { upgradePackage, retopupPackage, isLoading } = useContract();

  const handleUpgrade = async (packageId) => {
    if (isProcessing) return;

    const prevPackage = packages.find(p => p.id === packageId - 1);
    if (packageId > 1 && (!prevPackage || !prevPackage.isActive)) {
      addToast('error', 'Activate previous unit first');
      return;
    }

    setIsProcessing(true);
    setProcessingId(packageId);
    try {
      const result = await upgradePackage(packageId, userId);
      if (result.success) {
        addToast('success', `${PACKAGE_INFO[packageId].name} unit activated!`);
        setTimeout(onSuccess, 2000);
      } else {
        addToast('error', result.error || 'Activation failed');
      }
    } catch (error) {
      addToast('error', 'Activation failed');
    } finally {
      setIsProcessing(false);
      setProcessingId(null);
    }
  };

  const handleRetopup = async (packageId) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setProcessingId(packageId);
    try {
      const result = await retopupPackage(packageId, userId);
      if (result.success) {
        addToast('success', 'Retopup successful!');
        setTimeout(onSuccess, 2000);
      } else {
        addToast('error', result.error || 'Retopup failed');
      }
    } catch (error) {
      addToast('error', 'Retopup failed');
    } finally {
      setIsProcessing(false);
      setProcessingId(null);
    }
  };

  if (isLoading) {
    return <Loader message="Processing..." />;
  }

  return (
    <div className="units-page">
      <div className="units-header animate-fadeInUp">
        <img src="/assets/gifs/4.gif" alt="Units" className="header-gif" />
        <h2>Time Units</h2>
        <p>Activate units to increase your earning potential</p>
      </div>

      <div className="units-grid">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pkgId) => {
          const info = PACKAGE_INFO[pkgId];
          const pkgData = packages.find(p => p.id === pkgId);
          const isActive = pkgData?.isActive;
          const prevActive = pkgId === 1 || packages.find(p => p.id === pkgId - 1)?.isActive;
          const canActivate = !isActive && prevActive;
          const isCurrentProcessing = processingId === pkgId;

          return (
            <div 
              key={pkgId} 
              className={`unit-card ${isActive ? 'active' : ''} ${canActivate ? 'available' : ''} animate-fadeInUp`}
              style={{ '--delay': `${pkgId * 0.05}s`, '--unit-color': info.color }}
            >
              <div className="unit-header">
                <span className="unit-icon">{info.icon}</span>
                <div className="unit-info">
                  <span className="unit-name">{info.name}</span>
                  <span className="unit-price">${info.price} USDT</span>
                </div>
                {isActive && <span className="active-badge">✓</span>}
              </div>

              {isActive && pkgData && (
                <div className="unit-stats">
                  <div className="stat-row">
                    <span>Invested</span>
                    <span className="stat-val">${formatNumber(pkgData.totalInvested)}</span>
                  </div>
                  <div className="stat-row">
                    <span>Earned</span>
                    <span className="stat-val green">${formatNumber(pkgData.totalEarned)}</span>
                  </div>
                  {!pkgData.unlimitedCeiling && (
                    <>
                      <div className="stat-row">
                        <span>Remaining</span>
                        <span className="stat-val">${formatNumber(pkgData.remainingLimit)}</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(parseFloat(pkgData.totalEarned) / parseFloat(pkgData.ceilingLimit)) * 100}%`
                          }}
                        />
                      </div>
                    </>
                  )}
                  {pkgData.unlimitedCeiling && (
                    <div className="unlimited-badge">🌟 Unlimited</div>
                  )}
                </div>
              )}

              <div className="unit-action">
                {isActive ? (
                  <button 
                    className="action-btn retopup"
                    onClick={() => handleRetopup(pkgId)}
                    disabled={isProcessing}
                  >
                    {isCurrentProcessing ? (
                      <span className="btn-spinner"></span>
                    ) : (
                      <>🔄 Retopup ${info.price}</>
                    )}
                  </button>
                ) : canActivate ? (
                  <button 
                    className="action-btn activate"
                    onClick={() => handleUpgrade(pkgId)}
                    disabled={isProcessing}
                  >
                    {isCurrentProcessing ? (
                      <span className="btn-spinner"></span>
                    ) : (
                      <>⚡ Activate</>
                    )}
                  </button>
                ) : (
                  <button className="action-btn locked" disabled>
                    🔒 Locked
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="units-info animate-fadeInUp">
        <div className="info-card">
          <span className="info-icon">💡</span>
          <p>Each unit has a 3x earning ceiling. Retopup to extend your limit.</p>
        </div>
      </div>
    </div>
  );
};

export default Units;
